import { useEffect, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  Button,
  MultiSelect,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useCreateJourney } from '@/api/customer-journey';
import { useProductGroupOptions } from '@/api/product-groups';
import { useMe } from '@/api/me';
import { errorMessage } from './sale-process-error';

/**
 * Create-journey slide-over. Collects the fields the legacy AddCustomerJourney
 * modal exposes — interested services + (optional) a contact — and posts
 * `{ contact_id, customer_journey_explaination: { sale_tracking_id, service_interestings } }`.
 * The step defaults to 'searching' server-side; the sale-tracking owner defaults
 * to the current user (matching legacy, which seeds it from the signed-in
 * employee). Mutation invalidates the journeys list on success.
 */
export function CreateJourneyDrawer({
  customerId,
  contactId,
  open,
  onOpenChange,
}: {
  customerId: number;
  contactId?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const [services, setServices] = useState<string[]>([]);
  const mutation = useCreateJourney(customerId);
  const me = useMe();
  const meEmployeeId = me.data?.employee?.employee_id;
  const { data: serviceOptions = [] } = useProductGroupOptions();

  useEffect(() => {
    if (open) {
      setServices([]);
      mutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = () => {
    const payload: Record<string, unknown> = {
      customer_journey_explaination: {
        // Legacy seeds the sale-tracking owner with the current employee.
        sale_tracking_id: meEmployeeId ? [meEmployeeId] : [],
        service_interestings: services,
      },
    };
    if (contactId) payload.contact_id = contactId;
    mutation.mutate(payload, {
      onSuccess: () => {
        toast.success('Journey created');
        onOpenChange(false);
      },
      onError: (err) => toast.error('Failed to create journey', { description: errorMessage(err) }),
    });
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent side="right" size="md">
        <DrawerHeader>
          <DrawerTitle>Add journey</DrawerTitle>
          <DrawerDescription>
            Start a new sales journey for this customer. It begins at the Searching step.
          </DrawerDescription>
        </DrawerHeader>

        <DrawerBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? (
            <Alert tone="danger" title="Could not create journey">{errorMessage(mutation.error)}</Alert>
          ) : null}

          <Field label="Interested services">
            {() => (
              <MultiSelect
                placeholder="Select services"
                options={serviceOptions}
                value={services}
                onChange={setServices}
              />
            )}
          </Field>
        </DrawerBody>

        <DrawerFooter style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-2)' }}>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} loading={mutation.isPending}>
            Create journey
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export default CreateJourneyDrawer;
